import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { DashboardLogin } from "@/components/dashboard/DashboardLogin";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { SignupsSection } from "@/components/dashboard/SignupsSection";
import { BookingsSection } from "@/components/dashboard/BookingsSection";
import { AIPerformanceSection } from "@/components/dashboard/AIPerformanceSection";

interface BookingRecord {
  id: number;
  created_at: string;
  prompt: string;
  status: 'success' | 'failed';
  error_message?: string;
  attendee_name: string;
  scheduled_time: string;
  organizer_email: string;
  attendee_email: string;
  user_prompt: string;
}

interface UserRecord {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  raw_user_meta_data: {
    avatar_url?: string;
    email?: string;
    full_name?: string;
    iss?: string;
    name?: string;
    picture?: string;
    provider_id?: string;
  };
}

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const { toast } = useToast();
  const [stats, setStats] = useState({
    total: 0,
    successful: 0,
    failed: 0
  });
  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [isBookingsOpen, setIsBookingsOpen] = useState(false);

  useEffect(() => {
    const sessionAuth = sessionStorage.getItem('dashboard-auth');
    if (sessionAuth === 'true') {
      setIsAuthenticated(true);
      fetchBookings();
      fetchUsers();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      console.log("Fetching users from Supabase...");
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        throw profilesError;
      }

      if (profilesData) {
        const transformedUsers: UserRecord[] = profilesData.map(profile => ({
          id: profile.id,
          email: profile.email,
          created_at: profile.created_at,
          last_sign_in_at: profile.created_at,
          raw_user_meta_data: {
            avatar_url: profile.avatar_url || undefined,
            email: profile.email,
            full_name: profile.full_name || undefined,
          }
        }));

        console.log("Users transformed successfully:", transformedUsers);
        setUsers(transformedUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch users. Please ensure the profiles table exists in your database.",
        variant: "destructive",
      });
    }
  };

  const fetchBookings = async () => {
    try {
      console.log("Fetching bookings from Supabase...");
      const { data: bookingData, error } = await supabase
        .from('booking_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      if (bookingData) {
        console.log("Bookings fetched successfully:", bookingData);
        setBookings(bookingData);
        setStats({
          total: bookingData.length,
          successful: bookingData.filter(b => b.status === 'success').length,
          failed: bookingData.filter(b => b.status === 'failed').length
        });
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: config } = await supabase
        .from('config')
        .select('value')
        .eq('key', 'DASHBOARD_PASSWORD')
        .single();

      if (config && config.value === password) {
        setIsAuthenticated(true);
        sessionStorage.setItem('dashboard-auth', 'true');
        fetchBookings();
      } else {
        toast({
          title: "Access Denied",
          description: "Incorrect password",
          variant: "destructive",
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error checking password:', error);
      toast({
        title: "Error",
        description: "Failed to verify password",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('dashboard-auth');
    setIsAuthenticated(false);
  };

  const isAdmin = window.location.hostname === 'localhost' || 
                 window.location.hostname === 'meetalfie.ai' ||
                 window.location.hostname.endsWith('.netlify.app') ||
                 window.location.hostname.endsWith('.lovableproject.com');

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <DashboardLogin 
        password={password}
        setPassword={setPassword}
        handleLogin={handleLogin}
      />
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-8">
      <DashboardHeader onLogout={handleLogout} />
      
      <AIPerformanceSection />
      
      <SignupsSection 
        users={users}
        isOpen={isUsersOpen}
        onOpenChange={setIsUsersOpen}
      />

      <BookingsSection 
        bookings={bookings}
        stats={stats}
        isOpen={isBookingsOpen}
        onOpenChange={setIsBookingsOpen}
      />
    </div>
  );
};

export default Dashboard;
